import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { personName } from '@/i18n/personName';
import { ErrorCodes, isApiError, isNotFound, standingRefusal } from '@/api/errors';
import { meetingsApi, type VirtualAccountStandingSummary } from '@/api/meetings';
import { ErrorState } from '@/components/ErrorState';
import { EditIcon, HistoryIcon } from '@/components/icons';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';
import { VersionHistoryDialog } from './VersionHistoryDialog';

/**
 * The virtual account's standing summary (TMMAPP-0014; restyled by TMMAPP-0108, audit 06 § A11, v2
 * L624–637): a card with the "Summary" overline, the pen (a writer edits it) and, D1, the history
 * icon; the text at 13px line-height 1.7, or "Nothing recorded yet. …" when there is none.
 *
 * A save sends the `version` it was edited from (TMMAPI-0146). On `409 VERSION_CONFLICT` the panel
 * refetches and keeps the person's draft in the editor, the server's current text shown above it; the
 * next save sends that version. A draft is never discarded.
 */
interface VirtualAccountSummaryPanelProps {
  virtualAccountId: number;
  owner: boolean;
  onNotify: (message: string) => void;
}

export function VirtualAccountSummaryPanel({
  virtualAccountId,
  owner,
  onNotify,
}: VirtualAccountSummaryPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const [summary, setSummary] = useState<VirtualAccountStandingSummary | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [versions, setVersions] = useState<VirtualAccountStandingSummary[] | null>(null);
  const [saving, setSaving] = useState(false);
  // Someone else's newer summary, shown above the draft after a version conflict.
  const [conflict, setConflict] = useState<VirtualAccountStandingSummary | null>(null);
  // The summary being edited is dirty once its text differs from the saved one; a save in flight
  // is not dismissible (Esc does not show the discard question).
  const guard = useDialogDirtyGuard(
    editing && !sameValues(draft, summary?.body ?? ''),
    () => setEditing(false),
    saving,
    0,
    editing,
  );

  const load = useCallback(async () => {
    try {
      const next = await meetingsApi.getVirtualAccountSummary(virtualAccountId);
      setSummary(next);
      setLoadFailure(null);
    } catch (error) {
      if (isApiError(error) && isNotFound(error)) {
        setSummary({ body: '', authorDisplayName: null, createdAt: null, version: null });
        setLoadFailure(null);
      } else {
        setLoadFailure({ error });
      }
    }
  }, [virtualAccountId]);

  const lastFetch = useRef<number | null>(null);
  useEffect(() => {
    if (lastFetch.current === virtualAccountId) return;
    lastFetch.current = virtualAccountId;
    void load();
  }, [virtualAccountId, load]);

  async function handleSave() {
    if (saving) return;
    setSaving(true);
    try {
      const saved = await meetingsApi.putVirtualAccountSummary(
        virtualAccountId,
        draft,
        summary?.version ?? 0,
      );
      setSummary(saved);
      setConflict(null);
      setEditing(false);
      onNotify(t('meetings.summarySaved'));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.VERSION_CONFLICT) {
        // Refetch; the draft stays in the editor and the next save is based on the current version.
        try {
          const current = await meetingsApi.getVirtualAccountSummary(virtualAccountId);
          setSummary(current);
          setConflict(current);
        } catch {
          onNotify(t('meetings.errorGeneric'));
        }
      } else if (standingRefusal(error) === null) {
        onNotify(t('meetings.errorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleVersions() {
    try {
      setVersions(await meetingsApi.listVirtualAccountSummaryVersions(virtualAccountId));
    } catch {
      onNotify(t('meetings.errorGeneric'));
    }
  }

  if (summary === null) {
    if (loadFailure === null) return null;
    return (
      <Box component="section">
        <ErrorState
          variant="section"
          error={loadFailure.error}
          what={t('errors.what.virtualAccountSummary')}
          onRetry={() => void load()}
        />
      </Box>
    );
  }
  const empty = summary.body.trim().length === 0;
  const iconSx = {
    width: 28,
    height: 28,
    color: tokens.textLabel('surface'),
    '&:hover': { bgcolor: tokens.line, color: tokens.link },
  };

  return (
    <Paper component="section" aria-labelledby="va-summary-title" sx={{ p: '16px 20px' }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Typography
          id="va-summary-title"
          variant="overline"
          component="h2"
          sx={{ flex: 1, color: tokens.textLabel('surface') }}
        >
          {t('meetings.summaryLabel')}
        </Typography>
        {owner && (
          <IconButton
            aria-label={t('meetings.editSummary')}
            sx={iconSx}
            onClick={() => {
              setDraft(summary.body);
              setConflict(null);
              setEditing(true);
            }}
          >
            <EditIcon size={15} />
          </IconButton>
        )}
        <IconButton
          aria-label={t('meetings.summaryVersions')}
          sx={iconSx}
          onClick={() => void handleVersions()}
        >
          <HistoryIcon size={15} />
        </IconButton>
      </Stack>
      {empty ? (
        <Typography sx={{ mt: '6px', fontSize: 13, lineHeight: 1.6, color: tokens.text2 }}>
          {t('meetings.summaryEmpty')}
        </Typography>
      ) : (
        <>
          <Typography
            sx={{
              mt: '6px',
              fontSize: 13,
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              textWrap: 'pretty',
            }}
          >
            {summary.body}
          </Typography>
          {(summary.authorDisplayName || summary.createdAt) && (
            <Typography variant="caption" component="p" sx={{ color: tokens.text2, mt: 0.5 }}>
              {[
                summary.authorDisplayName,
                summary.createdAt ? formatInstant(summary.createdAt, locale, zone) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </>
      )}
      {editing && (
        <GuardedDialog guard={guard} titleId="summary-edit" fullWidth maxWidth="sm">
          <DialogTitle id="summary-edit">{t('meetings.editSummary')}</DialogTitle>
          <DialogContent>
            {conflict !== null && (
              <Box data-summary-conflict="" sx={{ mt: 1 }}>
                <Typography
                  variant="overline"
                  component="p"
                  sx={{ color: tokens.textLabel('surface') }}
                >
                  {t('meetings.summaryTheirVersion')}
                </Typography>
                <Typography
                  component="div"
                  sx={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', mb: 1 }}
                >
                  {conflict.body}
                </Typography>
                <Alert severity="warning">{t('meetings.summaryConflict')}</Alert>
              </Box>
            )}
            <TextField
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              multiline
              minRows={6}
              fullWidth
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <SubmitButton loading={saving} onClick={() => void handleSave()}>
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        </GuardedDialog>
      )}
      {versions !== null && (
        <VersionHistoryDialog
          titleId="summary-versions"
          rows={versions.map((row, index) => ({
            key: `${row.version ?? index}`,
            version: row.version,
            text: row.body,
            meta: [
              personName(row.authorDisplayName, t),
              row.createdAt ? formatInstant(row.createdAt, locale, zone) : null,
            ]
              .filter(Boolean)
              .join(' · '),
          }))}
          onClose={() => setVersions(null)}
        />
      )}
    </Paper>
  );
}
